import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  hoverColor: string;
  path: string;
}

interface ToolWithCategory extends Tool {
  category: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  tools: Tool[];
}

const categories: Category[] = [
  {
    id: 'data-format',
    name: '数据格式化',
    description: 'JSON、XML、CSV 等数据格式处理工具',
    icon: '📊',
    color: 'bg-blue-500',
    tools: [
      {
        id: 'json-formatter',
        name: 'JSON 在线格式化',
        description: '格式化、压缩、验证 JSON 数据',
        icon: '📄',
        color: 'bg-blue-500',
        hoverColor: 'hover:bg-blue-600',
        path: '/tools/json-formatter'
      },
      {
        id: 'xml-formatter',
        name: 'XML 在线格式化',
        description: '格式化、压缩、验证 XML 数据',
        icon: '📋',
        color: 'bg-green-500',
        hoverColor: 'hover:bg-green-600',
        path: '/tools/xml-formatter'
      },
      {
        id: 'json-to-xml',
        name: 'JSON转XML',
        description: '将JSON数据转换为XML格式',
        icon: '🔄',
        color: 'bg-indigo-500',
        hoverColor: 'hover:bg-indigo-600',
        path: '/tools/json-to-xml'
      },
      {
        id: 'xml-to-json',
        name: 'XML转JSON',
        description: '将XML数据转换为JSON格式',
        icon: '🔄',
        color: 'bg-indigo-500',
        hoverColor: 'hover:bg-indigo-600',
        path: '/tools/xml-to-json'
      }
    ]
  },
  {
    id: 'encoding',
    name: '编码解码',
    description: 'Base64、URL 等编码转换工具',
    icon: '🔐',
    color: 'bg-purple-500',
    tools: [
      {
        id: 'base64',
        name: 'Base64 编解码',
        description: 'Base64 编码和解码工具',
        icon: '🔐',
        color: 'bg-purple-500',
        hoverColor: 'hover:bg-purple-600',
        path: '/tools/base64'
      },
      {
        id: 'url-encoder',
        name: 'URL 编解码',
        description: 'URL 编码和解码工具',
        icon: '🔗',
        color: 'bg-orange-500',
        hoverColor: 'hover:bg-orange-600',
        path: '/tools/url-encoder'
      },
      {
        id: 'md5',
        name: 'MD5 加解密',
        description: 'MD5加密、解密查询和批量计算',
        icon: '🔐',
        color: 'bg-red-500',
        hoverColor: 'hover:bg-red-600',
        path: '/tools/md5'
      },
      {
        id: 'regex',
        name: '正则表达式',
        description: '正则表达式测试、常用示例和学习帮助',
        icon: '🔍',
        color: 'bg-purple-500',
        hoverColor: 'hover:bg-purple-600',
        path: '/tools/regex'
      }
    ]
  },
  {
    id: 'text-analysis',
    name: '文本分析',
    description: '文本统计、分析工具',
    icon: '📊',
    color: 'bg-teal-500',
    tools: [
      {
        id: 'character-counter',
        name: '字符统计工具',
        description: '统计字符数、单词数、行数等信息',
        icon: '📊',
        color: 'bg-teal-500',
        hoverColor: 'hover:bg-teal-600',
        path: '/tools/character-counter'
      },
      {
        id: 'content-diff',
        name: '内容对比工具',
        description: '对比两个文本内容的差异，高亮显示变化',
        icon: '🔍',
        color: 'bg-teal-500',
        hoverColor: 'hover:bg-teal-600',
        path: '/tools/content-diff'
      }
    ]
  },
  {
    id: 'image-tools',
    name: '图片处理',
    description: '图片转换、编码工具',
    icon: '🖼️',
    color: 'bg-pink-500',
    tools: [
      {
        id: 'image-to-base64',
        name: '图片转Base64',
        description: '将图片转换为Base64编码',
        icon: '🔄',
        color: 'bg-pink-500',
        hoverColor: 'hover:bg-pink-600',
        path: '/tools/image-to-base64'
      }
    ]
  },




];

const Home: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const allTools: ToolWithCategory[] = categories.flatMap(category => 
    category.tools.map(tool => ({ ...tool, category: category.name }))
  );

  const filteredTools = allTools.filter(tool => {
    const matchesSearch = !searchTerm || 
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !selectedCategory || 
      categories.find(cat => cat.id === selectedCategory)?.tools.some(t => t.id === tool.id);
    
    return matchesSearch && matchesCategory;
  });

  const currentCategory = selectedCategory ? categories.find(cat => cat.id === selectedCategory) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-64 bg-white shadow-lg min-h-screen">
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-8">在线工具集</h1>
            
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索工具..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Category Menu */}
            <nav className="space-y-2">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchTerm('');
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors ${
                  selectedCategory === null
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <span className="text-lg mr-3">🏠</span>
                  <span className="font-medium">全部工具</span>
                </div>
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                  {allTools.length}
                </span>
              </button>
              
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSearchTerm('');
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-lg mr-3">{category.icon}</span>
                    <span className="font-medium">{category.name}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full mr-2">
                      {category.tools.length}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  {searchTerm ? '搜索结果' : selectedCategory ? currentCategory?.name : '全部工具'}
                </h2>
                <p className="text-gray-600">
                  {searchTerm 
                    ? `找到 ${filteredTools.length} 个相关工具`
                    : selectedCategory 
                    ? currentCategory?.description
                    : '所有可用的在线工具'
                  }
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {selectedCategory && (
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    查看全部工具
                  </button>
                )}
                <span className="text-sm text-gray-500">
                  共 {filteredTools.length} 个工具
                </span>
              </div>
            </div>
          </div>

          {/* Tools Grid */}
          {filteredTools.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {filteredTools.map((tool) => (
                <Link key={tool.id} to={tool.path} className="group block">
                  <div className={`${tool.color} ${tool.hoverColor} transition-all duration-300 transform group-hover:scale-105 rounded-lg p-4 text-white shadow-md hover:shadow-lg`}>
                    <div className="text-2xl mb-2">{tool.icon}</div>
                    <h3 className="text-sm font-semibold mb-1 line-clamp-2">{tool.name}</h3>
                    <p className="text-blue-100 text-xs leading-relaxed mb-2 line-clamp-2">{tool.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-blue-200 opacity-75 truncate">
                        {tool.category}
                      </div>
                      <svg className="w-3 h-3 text-blue-200 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {searchTerm ? '未找到相关工具' : '该分类暂无工具'}
              </h3>
              <p className="text-gray-600">
                {searchTerm ? '请尝试其他关键词' : '敬请期待更多工具'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-16 text-center text-gray-500">
            <p className="mb-2">更多工具正在开发中，敬请期待...</p>
            <div className="flex items-center justify-center space-x-4 text-sm">
              <span>© 2024 在线工具集</span>
              <span>•</span>
              <a 
                href="https://beian.miit.gov.cn/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                湘ICP备2023018816号-1
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
